import { environmentActions } from '../../domain/environmentActions.js';
import { ACTIONABLE, isActionable, type ActionableStatus, type SyncStatus } from '../../domain/status.js';
import type { MatrixColumn } from './types.js';

/** The statuses a column header counts, in the order it draws them: the domain's actionable ones. */
export const HEADER_STATUSES = ACTIONABLE;

/** The slice of a grid row the header reads: its own status per environment. */
export type HeaderRow = { cells: Record<string, { status: SyncStatus } | undefined> };

export type ColumnHeaderView = {
    /** Every drift and missing record in the environment, all types — what *Push all drift & missing* writes. */
    actionable: number;
    /** Every env-only record in the environment, all types — the Inbox. */
    inboxCount: number;
    /** Never pulled, last pull failed, or nothing differs: the column menu's Push is disabled. */
    nothingToPush: boolean;
    /** Drift and missing among the rows on screen, in this column; null while the environment was never pulled. */
    shown: Record<ActionableStatus, number> | null;
};

/**
 * What one environment column's header says. The menu's Push and Inbox are environment-wide,
 * so their counts come from the last pull, every type summed. The glyphs count only the rows
 * the grid shows — the same rows, filtered the same way, judged live like the cells, by the
 * same `isActionable` the toolbar's *Needs attention* uses — so the header can never say
 * `268 drift` above one row. Env-only is never a glyph: both grids list master records only,
 * so no row on screen can be env-only; the menu's Inbox N carries it.
 */
export const columnHeaderView = (
    column: Pick<MatrixColumn, 'name' | 'counts' | 'pullFailed'>,
    rows: readonly HeaderRow[],
): ColumnHeaderView => {
    const counts = column.counts;
    const { actionable, inbox, canPush } = environmentActions(counts, column.pullFailed);
    return {
        actionable,
        inboxCount: inbox,
        nothingToPush: !canPush,
        shown: counts ? shownCounts(column.name, rows) : null,
    };
};

const shownCounts = (environment: string, rows: readonly HeaderRow[]): Record<ActionableStatus, number> => {
    const shown = { drift: 0, missing: 0 } satisfies Record<ActionableStatus, number>;
    for (const row of rows) {
        const status = row.cells[environment]?.status;
        if (status !== undefined && isActionable(status)) shown[status] += 1;
    }
    return shown;
};
